import React from "react";
import { DataTable } from "@/components/DataTable";
import { EmptyState } from "@/components/DataTable/States/Empty";
import { CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCompletedEventsColumns } from "./hooks/useCompletedEventsColumns";
import { CompletedEventsTableHeader } from "./CompletedEventsTableHeader";
import { useLiveEventsTable } from "./hooks/useLiveEventsTable";
import { LiveEventStatusEnum, LiveEvent } from "@/__generated__/types";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";

export const CompletedEventsTab = () => {
  const { t } = useTranslation();
  const columns = useCompletedEventsColumns();
  const { navToRoute } = useProtectedNavigation();
  const tableData = useLiveEventsTable(LiveEventStatusEnum.Completed);
  const {
    eventsData,
    loading,
    hasNextPage,
    hasPreviousPage,
    pageIndex,
    pageCount,
    handleFilterApply,
    getFilterDisplayValue,
    handleNextPage,
    handlePreviousPage,
    availableChatChannels,
  } = tableData;

  return (
    <DataTable<LiveEvent>
      data={eventsData}
      columns={columns}
      isLoading={loading}
      pagination={
        <>
          <DataTable.PaginationLabel
            pageCount={pageCount}
            pageIndex={pageIndex}
          />
          <DataTable.PaginationControls
            onNextPage={hasNextPage ? handleNextPage : false}
            onPreviousPage={hasPreviousPage ? handlePreviousPage : false}
          />
        </>
      }
      tableHeader={<DataTable.THeader />}
      tableBody={<DataTable.TBody />}
      emptyState={
        <EmptyState
          copy={t("gamesAndLiveEvents.table.completed.emptyState")}
          buttonCopy={t("gamesAndLiveEvents.table.completed.createNewEvent")}
          buttonTarget={() => navToRoute("editEvent")}
          icon={
            <CheckCircle
              className="h-[36px] w-[36px] text-primary-light"
              aria-label={t(
                "gamesAndLiveEvents.table.completed.createNewEvent",
              )}
            />
          }
        />
      }
      topHeader={
        <CompletedEventsTableHeader
          onFilterChange={handleFilterApply}
          filterDisplayValue={getFilterDisplayValue()}
          availableChatChannels={availableChatChannels}
        />
      }
    />
  );
};
